import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import type { Moment } from 'moment';
import { Goal, Change } from '../../../models';
import { formatMomentDate } from '../../../locales';

const PHASE_CLASSES = [
    'bg-sky-400/80',
    'bg-amber-400/80',
    'bg-emerald-400/80',
    'bg-violet-400/80',
    'bg-rose-400/80'
];

function GoalPlanningStrip({ goal }: { goal: Goal }) {
    const { t, i18n } = useTranslation();
    const bounds = goal.planningRange();
    const span = bounds.stop.valueOf() - bounds.start.valueOf();
    if (!(span > 0))
        return null;

    const percent = (date?: Moment | null) => {
        if (!date?.isValid())
            return null;
        return Math.min(100, Math.max(0, (date.valueOf() - bounds.start.valueOf()) / span * 100));
    };

    const todayPercent = percent(moment());
    const showToday = todayPercent != null && todayPercent > 0 && todayPercent < 100;

    const deadlineLabel = (change: Change) => {
        const title = change.localizedTitle(i18n.language) || '';
        return `${change.slugName()} ${title}`.trim();
    };

    return (
        <div className="border rounded p-2 pb-4 mb-4">
            <h3 className="font-bold mb-3">{t('goals.planning')}</h3>
            <div className="relative h-56 mx-3">
                <div className="absolute left-0 right-0 top-40 h-3 rounded bg-gray-100 dark:bg-gray-700" />
                {(goal.phases || []).map((phase, index) => {
                    if (!phase.startAt || !phase.stopAt)
                        return null;
                    const left = percent(phase.startAt);
                    const right = percent(phase.stopAt);
                    if (left == null || right == null)
                        return null;
                    const width = Math.max(right - left, 0.8);
                    return (
                        <div key={phase._id}>
                            <div
                                className={`absolute top-40 z-10 h-3 rounded ${PHASE_CLASSES[index % PHASE_CLASSES.length]}`}
                                style={{ left: `${left}%`, width: `${width}%` }}
                                title={phase.name}
                            />
                            <span
                                className="absolute top-44 z-10 truncate text-[10px] text-gray-600 dark:text-gray-300"
                                style={{ left: `${left}%`, width: `${Math.max(width, 8)}%` }}
                            >
                                {phase.name}
                            </span>
                        </div>
                    );
                })}
                {(goal.changes || []).filter((change) => change.deadline).map((change) => {
                    const left = percent(change.deadline);
                    if (left == null)
                        return null;
                    return (
                        <div
                            key={change._id}
                            className="absolute top-[10.35rem] z-20 h-2 w-2 -ml-1 rounded-full bg-gray-900 dark:bg-white border border-white dark:border-gray-900"
                            style={{ left: `${left}%` }}
                            title={deadlineLabel(change)}
                        />
                    );
                })}
                {showToday && (
                    <div
                        className="absolute top-32 bottom-8 z-30 w-px bg-violet-600"
                        style={{ left: `${todayPercent}%` }}
                        title={t('common.today') || ''}
                    />
                )}
                {(goal.milestones || []).map((milestone) => {
                    const left = percent(milestone.at);
                    if (left == null)
                        return null;
                    const anchorEnd = left > 82;
                    return (
                        <div key={milestone._id} className="absolute top-[10.375rem] z-40" style={{ left: `${left}%` }}>
                            <div
                                className="h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-blue-600"
                                title={milestone.name}
                            />
                            <span
                                className={`absolute bottom-1 whitespace-nowrap text-[10px] leading-none text-gray-600 dark:text-gray-300 -rotate-45 ${anchorEnd ? 'right-0 origin-bottom-right pr-4' : 'left-0 origin-bottom-left pl-4'}`}
                                title={milestone.name}
                            >
                                {milestone.name}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="mt-1 flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
                <span>{formatMomentDate(bounds.start, 'LL')}</span>
                <span>{formatMomentDate(bounds.stop, 'LL')}</span>
            </div>
        </div>
    );
}

export default GoalPlanningStrip;
